import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  FileText,
  ArrowRight,
  Mail,
  Receipt,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getMyPayments } from "@/data/requests/dashboard-requests";
import ListView from "@/components/list-view";

export function MyPaymentsScreen() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data: myPaymentsData, isLoading } = useQuery({
    queryKey: ["myPayments"],
    queryFn: getMyPayments,
    select: (data) => data.data,
    onError: (error) => {
      toast({
        title: "Error loading payments",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  const pageSettings = {
    title: "Benim Payment'larım",
    actions: [],
  };

  const columns = [
    {
      key: "name",
      label: "Payment Adı",
      accessor: (item) => item.name || "",
    },
    {
      key: "invoiceId",
      label: "Invoice ID",
      accessor: (item) => item.invoiceId || "",
    },
    {
      key: "type",
      label: "Tip",
      render: (row) => (
        <Badge
          variant={row.type === "FIXED" ? "secondary" : "outline"}
          className="text-xs"
        >
          {row.type || "-"}
        </Badge>
      ),
    },
    {
      key: "commissionCount",
      label: "Commission",
      render: (row) => (
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          {row.statistics?.commissionCount || 0}
        </span>
      ),
    },
    {
      key: "mailSendedCount",
      label: "Mail Gönderildi",
      render: (row) => (
        <span className="flex items-center gap-1">
          <Mail className="w-3 h-3" />
          {row.statistics?.mailSendedCount || 0}
        </span>
      ),
    },
    {
      key: "detailsCount",
      label: "Fatura",
      render: (row) => (
        <span className="flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {row.statistics?.detailsCount || 0}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => navigate(`/payments/${row.id}`)}
          >
            Detay
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
      style={{ padding: "0 10px" }}
    >
      {(!myPaymentsData || myPaymentsData.payments?.length === 0) ? (
        <div className="text-center py-8 text-muted-foreground">
          <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm">Henüz size ait payment bulunmuyor.</p>
          <p className="text-xs mt-1">Payment oluştururken "Related User" alanına adınızı yazın.</p>
        </div>
      ) : (
        <ListView
          isLoading={isLoading}
          data={myPaymentsData?.payments}
          columns={columns}
          totalItems={myPaymentsData?.payments?.length || 0}
          enablePagination={false}
          onRowClick={(item) => navigate(`/payments/${item.id}`)}
          renderGridItem={(item) => (
            <Card
              key={item.id}
              className="border-l-4 border-l-blue-400 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/payments/${item.id}`)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm text-foreground">
                        {item.name}
                      </h4>
                      <Badge variant="outline" className="text-xs">
                        {item.invoiceId}
                      </Badge>
                      {item.type && (
                        <Badge
                          variant={item.type === "FIXED" ? "secondary" : "outline"}
                          className="text-xs"
                        >
                          {item.type}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {item.statistics?.commissionCount || 0} commission
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {item.statistics?.mailSendedCount || 0} mail
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {item.statistics?.detailsCount || 0} fatura
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-2 h-8">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        />
      )}
    </AppLayout>
  );
}
